import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useSession } from '@/features/auth/session-context';
import { apiHooks } from '@/lib/api';
import { BranchContext, type BranchSelection } from './branch-context';

const STORAGE_KEY = 'ommri-care-admin.branch';

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Branch selection from the active role's scope (me.branches). "All" is only
 * offered when the role covers every branch. The choice is sent to the API as
 * X-Branch-Id (omitted for All).
 */
export function BranchProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const branches = useMemo(() => me?.branches ?? [], [me]);
  // "All" is the group view: Finance, Clinic manager and Super admin only.
  const canSelectAll = !!me?.canViewAllBranches;
  const [storedId, setStoredId] = useState<string | null>(readStored);

  const selected = useMemo<BranchSelection>(() => {
    if (storedId === 'all' && canSelectAll) return null;
    return branches.find((b) => b.id === storedId) ?? branches[0] ?? null;
  }, [storedId, branches, canSelectAll]);

  // Layout effect, not a passive one: child pages start their first queries in
  // passive effects, which run after every layout effect of the same commit.
  // With useEffect the first request after a cold load had no X-Branch-Id.
  useLayoutEffect(() => {
    const getBranchId = () => selected?.id ?? null;
    apiHooks.getBranchId = getBranchId;
    return () => {
      // A newer provider may already own the shared client hook (notably when
      // isolated app roots overlap briefly in tests). Do not clear its value.
      if (apiHooks.getBranchId === getBranchId)
        apiHooks.getBranchId = undefined;
    };
  }, [selected]);

  const select = useCallback((branchId: string | 'all') => {
    setStoredId(branchId);
    try {
      localStorage.setItem(STORAGE_KEY, branchId);
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo(
    () => ({ branches, selected, canSelectAll, select }),
    [branches, selected, canSelectAll, select],
  );
  return (
    <BranchContext.Provider value={value}>{children}</BranchContext.Provider>
  );
}
